import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { minutesToMilliseconds } from "date-fns";
import { useRef } from "react";

import { useCreateOrGetPlacementCandidate } from "../../PlacementCandidate/api/useCreateOrGetPlacementCandidate";
import { useIsJobsEnabled } from "../../PlacementCandidate/useIsJobsEnabled";
import { useFetchDiscoveryPlacements } from "../../Placements/api/useFetchDiscoveryPlacements";
import { useFetchPaginatedPlacements } from "../../Placements/api/useFetchPaginatedPlacements";

interface UseWorkplaceStaffJobsDataResult {
  isJobsEnabled: boolean;
  isLoading: boolean;
  placementsCount: number;
  hasMorePlacements: boolean;
  placementCandidateId: string | undefined;
}

export function useWorkplaceStaffJobsData(workplaceId: string): UseWorkplaceStaffJobsDataResult {
  const isJobsEnabled = useIsJobsEnabled();
  const worker = useDefinedWorker();

  const isDiscoveryMigrationEnabled = useCbhFlag(CbhFeatureFlag.PLACEMENTS_DISCOVERY_MIGRATION, {
    defaultValue: false,
  });

  const { data: placementCandidate, isError: isPlacementCandidateError } =
    useCreateOrGetPlacementCandidate(
      { workerId: worker.userId },
      { enabled: isJobsEnabled && isDefined(worker.userId) }
    );

  const isPlacementsQueryEnabled =
    isJobsEnabled && isDefined(worker.userId) && isDefined(placementCandidate);

  // We intentionally skip the user's distance / workerTypes filters that
  // useGetPlacementFilters would apply — the "View N staff jobs" button is a
  // workplace-level count, not a feed filter, and the endpoint is already
  // worker-scoped so eligibility is enforced server-side.
  //
  // The discovery endpoint's filter[matchedType] would be ideal for rejecting
  // cross-workplace "you might also like" suggestions server-side, but the
  // shared contract schema runs its `"exact" → MATCHED_TYPE.EXACT` transform
  // on the client as well, so `filter[matchedType]=0` ships on the wire and
  // the server's enum parser drops it. Tracked as a contract bug.
  // Until that's fixed, we rely on the workplaceIds filter + the client-side
  // workplace-id guard in the count aggregation below.
  const placementsFilter = { workplaceId };

  // 5-minute staleTime so re-opening the same workplace modal within a
  // session hits the cache instead of re-fetching. The count is not
  // real-time data.
  const staleTime = minutesToMilliseconds(5);

  const {
    data: legacyPlacementsData,
    isError: isLegacyPlacementsError,
    hasNextPage: hasMoreLegacyPlacements,
  } = useFetchPaginatedPlacements(
    {
      workerId: worker.userId,
      placementCandidateId: placementCandidate?.id ?? "",
      filter: placementsFilter,
      shouldLogEvent: false,
    },
    {
      enabled: isPlacementsQueryEnabled && !isDiscoveryMigrationEnabled,
      staleTime,
    }
  );

  const {
    data: discoveryPlacementsData,
    isError: isDiscoveryPlacementsError,
    hasNextPage: hasMoreDiscoveryPlacements,
  } = useFetchDiscoveryPlacements(
    {
      workerId: worker.userId,
      placementCandidateId: placementCandidate?.id ?? "",
      filter: placementsFilter,
      shouldLogEvent: false,
    },
    {
      enabled: isPlacementsQueryEnabled && isDiscoveryMigrationEnabled,
      staleTime,
    }
  );

  const placementsData = isDiscoveryMigrationEnabled
    ? discoveryPlacementsData
    : legacyPlacementsData;
  const isPlacementsError = isDiscoveryMigrationEnabled
    ? isDiscoveryPlacementsError
    : isLegacyPlacementsError;
  // Only one page is ever fetched here, so a truthy hasNextPage means the
  // real count exceeds what we aggregated — surfaced as "N+" on the button.
  const hasMorePlacements = Boolean(
    isDiscoveryMigrationEnabled ? hasMoreDiscoveryPlacements : hasMoreLegacyPlacements
  );

  const rawPlacementsCount =
    placementsData?.pages.reduce(
      (count, page) =>
        count +
        page.data.filter((placement) => placement.relationships.workplace.data.id === workplaceId)
          .length,
      0
    ) ?? 0;

  const hasError = isPlacementCandidateError || isPlacementsError;
  const hasResolvedNow = !isJobsEnabled || hasError || isDefined(placementsData);

  const hasResolvedRef = useRef(hasResolvedNow);
  if (hasResolvedNow) {
    hasResolvedRef.current = true;
  }

  const latchedCountRef = useRef(rawPlacementsCount);
  if (isDefined(placementsData)) {
    latchedCountRef.current = rawPlacementsCount;
  }

  const placementsCount = isDefined(placementsData) ? rawPlacementsCount : latchedCountRef.current;
  const isLoading = !hasResolvedRef.current;

  return {
    isJobsEnabled,
    isLoading,
    placementsCount,
    hasMorePlacements,
    placementCandidateId: placementCandidate?.id,
  };
}
